import { useState, useEffect } from "react";
import {
  Modal,
  Descriptions,
  Divider,
  List,
  Select,
  Button,
  Tag,
  Empty,
  message,
  Spin,
  Card,
  DatePicker,
  Input,
  InputNumber,
  Form,
} from "antd";
import { Calendar, Trash2, CheckCircle2, XCircle, AlertCircle, Ban, Moon, Save } from "lucide-react";
import dayjs from "dayjs";
import useGroups from "../../hooks/useGroups";
import useExcursions from "../../hooks/useExcursions";
import useUsers from "../../hooks/useUsers";
import DebtModal from "../transfers/DebtModal";

const { RangePicker } = DatePicker;

function GroupDetailModal({ visible, group, onClose, onUpdate }) {
  const { setExcursionToGroup, unsetExcursionFromGroup, updateGroup, loading: groupLoading } = useGroups();
  const { getExcursions, excursions, loading: excursionLoading } = useExcursions();
  const { getUsers, users } = useUsers();

  const [selectedExcursion, setSelectedExcursion] = useState(null);
  const [form] = Form.useForm();
  const [hasChanges, setHasChanges] = useState(false);
  const [debtModal, setDebtModal] = useState({
    open: false,
    userId: null,
    type: null,
    extraInfo: null,
    transferId: null,
  });

  const dateRange = Form.useWatch('dateRange', form);
  const nights = dateRange && dateRange[0] && dateRange[1]
    ? dateRange[1].diff(dateRange[0], 'day')
    : 0;

  useEffect(() => {
    if (visible && group) {
      // Cargar excursiones disponibles para el período del grupo
      getExcursions({
        day_after: group.checkInDate,
        day_before: group.checkOutDate,
      });

      // Cargar coordinadores de la empresa
      if (group.company?.id) {
        getUsers({ company_id: group.company.id });
      }

      // Inicializar formulario con datos del grupo
      form.setFieldsValue({
        dateRange: [dayjs(group.checkInDate), dayjs(group.checkOutDate)],
        coordinator: group.coordinator?.id,
        observation: group.observation,
        entryCoordinatorName: group.entryCoordinatorName,
        entryNumber: group.entryNumber,
        entryGroupObservation: group.entryGroupObservation,
        coordinatorCommissionRate: group.coordinatorCommissionRate,
        companyCommissionRate: group.companyCommissionRate,
      });
      setHasChanges(false);
    }
  }, [visible, group]);

  if (!group) return null;

  // Usar TODAS las excursiones del período sin filtrar (relación muchos a muchos)
  const availableExcursions = excursions.results || [];

  // Obtener IDs de excursiones asignadas a ESTE grupo (usando isScheduleForGroup)
  const assignedToThisGroupIds = (group.scheduledExcursions || [])
    .filter((exc) => exc.isScheduleForGroup)
    .map((exc) => exc.id);

  // Excursiones asignadas a este grupo (para mostrar en la lista)
  const assignedExcursions = (group.scheduledExcursions || [])
    .filter((exc) => exc.isScheduleForGroup);

  const handleAssignExcursion = async () => {
    if (!selectedExcursion) {
      message.warning("Selecciona una excursión");
      return;
    }

    await setExcursionToGroup(group.id, selectedExcursion);
    setSelectedExcursion(null);
    onUpdate?.();
  };

  const handleRemoveExcursion = async (excursionId) => {
    await unsetExcursionFromGroup(group.id, excursionId);
    onUpdate?.();
  };

  const handleSaveChanges = async () => {
    try {
      const values = await form.validateFields();
      const [checkInDate, checkOutDate] = values.dateRange;

      const updateData = {
        id: group.id,
        company: group.company.id,
        checkInDate: checkInDate.format("YYYY-MM-DD"),
        checkOutDate: checkOutDate.format("YYYY-MM-DD"),
        coordinator: values.coordinator,
        observation: values.observation || "",
        entryCoordinatorName: values.entryCoordinatorName || "",
        entryNumber: values.entryNumber || "",
        entryGroupObservation: values.entryGroupObservation || "",
        coordinatorCommissionRate: values.coordinatorCommissionRate,
        companyCommissionRate: values.companyCommissionRate,
      };

      await updateGroup(updateData);
      setHasChanges(false);
      onUpdate?.();
      message.success("Grupo actualizado correctamente");
    } catch (error) {
      console.error("Error al guardar:", error);
    }
  };

  const handleFormChange = () => {
    setHasChanges(true);
  };

  return (
    <>
    <Modal
      title={`Grupo: ${group.company?.name || "Sin empresa"}`}
      open={visible}
      onCancel={onClose}
      footer={[
        <Button
          key="save"
          type="primary"
          icon={<Save size={16} />}
          onClick={handleSaveChanges}
          loading={groupLoading.put}
          disabled={!hasChanges}
        >
          Guardar Cambios
        </Button>,
        <Button key="close" onClick={onClose}>
          Cerrar
        </Button>,
      ]}
      width={900}
      styles={{ body: { maxHeight: "80vh", overflowY: "auto" } }}
    >
      <Spin spinning={groupLoading.post || groupLoading.delete || groupLoading.put}>
        <Form
          form={form}
          layout="vertical"
          onValuesChange={handleFormChange}
          className="mt-4"
        >
          {/* Información Principal */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-gray-500 text-sm">Empresa</label>
              <p className="font-medium">{group.company?.name || "-"}</p>
            </div>

            <Form.Item
              label="Coordinador"
              name="coordinator"
              className="mb-0"
            >
              <Select
                placeholder="Seleccionar coordinador"
                options={users?.map((u) => ({
                  value: u.id,
                  label: `${u.firstName} ${u.lastName || ""}`,
                }))}
                allowClear
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <Form.Item
              label="Fechas de estadía"
              name="dateRange"
              className="mb-0"
            >
              <RangePicker
                format="DD/MM/YYYY"
                placeholder={["Check-in", "Check-out"]}
                className="w-full"
              />
            </Form.Item>

            <div>
              <label className="text-gray-500 text-sm">Noches</label>
              <p className="font-medium flex items-center gap-1">
                <Moon size={16} />
                {nights} {nights === 1 ? "noche" : "noches"}
              </p>
            </div>
          </div>

          <Form.Item
            label="Observación"
            name="observation"
            className="mb-4"
          >
            <Input.TextArea
              placeholder="Observaciones del grupo"
              rows={2}
            />
          </Form.Item>

          {/* Rebotero */}
          <Divider orientation="left" className="text-sm">Rebotero</Divider>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <Form.Item
              label="Nombre Rebotero"
              name="entryCoordinatorName"
              className="mb-0"
            >
              <Input placeholder="Nombre completo" />
            </Form.Item>

            <Form.Item
              label="Teléfono Rebotero"
              name="entryNumber"
              className="mb-0"
            >
              <Input placeholder="Número de teléfono" />
            </Form.Item>
          </div>

          <Form.Item
            label="Observación Rebotero"
            name="entryGroupObservation"
            className="mb-0"
          >
            <Input.TextArea
              placeholder="Observaciones del rebotero"
              rows={2}
            />
          </Form.Item>

          {/* Comisiones del Grupo */}
          <Divider orientation="left" className="text-sm">Comisiones del Grupo</Divider>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <Form.Item
              label="Comisión Coordinador (%)"
              name="coordinatorCommissionRate"
              className="mb-0"
            >
              <InputNumber min={0} max={100} className="w-full" addonAfter="%" />
            </Form.Item>
            <Form.Item
              label="Comisión Empresa (%)"
              name="companyCommissionRate"
              className="mb-0"
            >
              <InputNumber min={0} max={100} className="w-full" addonAfter="%" />
            </Form.Item>
          </div>
        </Form>

        {/* Sección de Rendiciones */}
        <Divider orientation="left">Historial de Rendiciones</Divider>

        {/* Rendiciones Pagadas */}
        {group.renditions?.paid?.length > 0 && (
          <div className="mb-4">
            {group.renditions.paid.map((rendition, idx) => (
              <Card
                key={rendition.id}
                size="small"
                className="mb-2 border-green-200 bg-green-50 cursor-pointer hover:border-green-400 transition-colors"
                onClick={() => {
                  setDebtModal({
                    open: true,
                    userId: null,
                    type: null,
                    extraInfo: null,
                    transferId: rendition.id,
                  });
                }}
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-green-700">
                    Rendición #{idx + 1} - {rendition.type === 'COORDINATOR' ? 'Coordinador' : 'Empresa'}
                  </span>
                  <Tag color="success">
                    ${rendition.amount?.toLocaleString()} - {dayjs(rendition.date).format('DD/MM/YYYY')}
                  </Tag>
                </div>
                {rendition.excursions?.length > 0 && (
                  <List
                    size="small"
                    dataSource={rendition.excursions}
                    renderItem={(exc) => (
                      <List.Item className="py-1 px-0">
                        <div className="flex justify-between w-full items-center">
                          <span className="text-sm text-gray-600">
                            <CheckCircle2 size={12} className="inline mr-1 text-green-500" />
                            {exc.name} - {dayjs(exc.day).format('DD/MM/YYYY')}
                          </span>
                          <Tag size="small">{exc.passengerCount} pax</Tag>
                        </div>
                      </List.Item>
                    )}
                  />
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Pendientes - Solo mostrar si hay algo realmente pendiente */}
        {(group.renditions?.pending?.coordinator?.status === 'PENDING' ||
          group.renditions?.pending?.coordinator?.status === 'PARTIAL' ||
          group.renditions?.pending?.company?.status === 'PENDING' ||
          group.renditions?.pending?.company?.status === 'PARTIAL' ||
          group.renditions?.pending?.excursions?.length > 0) && (
          <Card size="small" className="mb-4 border-orange-200 bg-orange-50/50">
            <div className="font-semibold mb-3 text-orange-700 text-base">Pendiente de Pago</div>

            {/* Status Coordinador y Empresa */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {/* Coordinador */}
              <div className="bg-white rounded-lg p-3 border border-gray-100">
                <div className="text-xs text-gray-500 mb-1">Coordinador</div>
                {(() => {
                  const coord = group.renditions?.pending?.coordinator;
                  if (!coord) return <span className="text-gray-400">-</span>;
                  if (coord.status === 'NO_COMMISSION') {
                    return <span className="text-gray-500 font-medium">Comisión 0%</span>;
                  }
                  if (coord.status === 'NO_PASSENGERS') {
                    return <span className="text-gray-500">Sin pasajeros</span>;
                  }
                  if (coord.status === 'PAID') {
                    return <span className="text-green-600 font-medium flex items-center gap-1"><CheckCircle2 size={14} /> Pagado</span>;
                  }
                  if (coord.status === 'PARTIAL') {
                    return (
                      <button
                        className="text-yellow-600 font-semibold hover:text-yellow-700 hover:underline flex items-center gap-1"
                        onClick={() => {
                          setDebtModal({
                            open: true,
                            userId: group.coordinator?.id,
                            type: 'USER_COORDINATOR',
                            extraInfo: group.id,
                          });
                        }}
                      >
                        Parcial <span className="text-xs font-normal">({coord.paidPassengers}/{coord.totalPassengers}) → Ver</span>
                      </button>
                    );
                  }
                  if (coord.status === 'PENDING') {
                    return (
                      <button
                        className="text-red-600 font-semibold hover:text-red-700 hover:underline flex items-center gap-1"
                        onClick={() => {
                          setDebtModal({
                            open: true,
                            userId: group.coordinator?.id,
                            type: 'USER_COORDINATOR',
                            extraInfo: group.id,
                          });
                        }}
                      >
                        ${coord.amount?.toLocaleString()} <span className="text-xs font-normal">→ Ver detalle</span>
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Empresa */}
              <div className="bg-white rounded-lg p-3 border border-gray-100">
                <div className="text-xs text-gray-500 mb-1">Empresa</div>
                {(() => {
                  const comp = group.renditions?.pending?.company;
                  if (!comp) return <span className="text-gray-400">-</span>;
                  if (comp.status === 'NO_COMMISSION') {
                    return <span className="text-gray-500 font-medium">Comisión 0%</span>;
                  }
                  if (comp.status === 'NO_PASSENGERS') {
                    return <span className="text-gray-500">Sin pasajeros</span>;
                  }
                  if (comp.status === 'PAID') {
                    return <span className="text-green-600 font-medium flex items-center gap-1"><CheckCircle2 size={14} /> Pagado</span>;
                  }
                  if (comp.status === 'PARTIAL') {
                    return (
                      <button
                        className="text-yellow-600 font-semibold hover:text-yellow-700 hover:underline flex items-center gap-1"
                        onClick={() => {
                          setDebtModal({
                            open: true,
                            userId: group.company?.id,
                            type: 'COMPANY',
                            extraInfo: group.id,
                          });
                        }}
                      >
                        Parcial <span className="text-xs font-normal">({comp.paidPassengers}/{comp.totalPassengers}) → Ver</span>
                      </button>
                    );
                  }
                  if (comp.status === 'PENDING') {
                    return (
                      <button
                        className="text-red-600 font-semibold hover:text-red-700 hover:underline flex items-center gap-1"
                        onClick={() => {
                          setDebtModal({
                            open: true,
                            userId: group.company?.id,
                            type: 'COMPANY',
                            extraInfo: group.id,
                          });
                        }}
                      >
                        ${comp.amount?.toLocaleString()} <span className="text-xs font-normal">→ Ver detalle</span>
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>

            {/* Tabla de excursiones pendientes */}
            {group.renditions?.pending?.excursions?.length > 0 && (
              <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Excursión</th>
                      <th className="text-left py-2 px-3 font-medium text-gray-600">Fecha</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-600">Pasajeros</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.renditions.pending.excursions.map((exc, idx) => (
                      <tr key={exc.id || idx} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                        <td className="py-2 px-3 text-gray-700">{exc.name}</td>
                        <td className="py-2 px-3 text-gray-500">{dayjs(exc.day).format('DD/MM/YYYY')}</td>
                        <td className="py-2 px-3 text-right">
                          <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs font-medium">
                            {exc.passengerCount} pax
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        )}

        {/* Sin pendientes y sin pagadas - mostrar mensaje */}
        {(!group.renditions?.paid?.length &&
          group.renditions?.pending?.coordinator?.status !== 'PENDING' &&
          group.renditions?.pending?.company?.status !== 'PENDING') && (
          <div className="text-gray-500 text-center py-4">
            {group.renditions?.pending?.coordinator?.status === 'NO_PASSENGERS' ||
             group.renditions?.pending?.company?.status === 'NO_PASSENGERS'
              ? 'Sin pasajeros en este grupo'
              : group.renditions?.pending?.coordinator?.status === 'NO_COMMISSION' &&
                group.renditions?.pending?.company?.status === 'NO_COMMISSION'
                ? 'Comisiones en 0% para coordinador y empresa'
                : 'No hay rendiciones registradas'}
          </div>
        )}

        {/* Fallback para formato anterior (sin renditions) */}
        {!group.renditions && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Rendición Coordinador">
              {(() => {
                const comm = group.coordinatorCommissionPaid;
                if (!comm || typeof comm !== 'object') {
                  if (comm === null) return <Tag color="default">Sin pasajeros</Tag>;
                  if (comm === true) return <Tag icon={<CheckCircle2 size={12} />} color="success">Pagada</Tag>;
                  return (
                    <Tag
                      icon={<XCircle size={12} />}
                      color="error"
                      className="cursor-pointer"
                      onClick={() => {
                        setDebtModal({
                          open: true,
                          userId: group.coordinator?.id,
                          type: 'USER_COORDINATOR',
                          extraInfo: group.id,
                        });
                      }}
                    >
                      Pendiente - Ver detalle
                    </Tag>
                  );
                }
                switch (comm.status) {
                  case 'NO_PASSENGERS':
                    return <Tag icon={<AlertCircle size={12} />} color="default">Sin pasajeros</Tag>;
                  case 'NO_COMMISSION':
                    return <Tag icon={<Ban size={12} />} color="default">Comisión 0%</Tag>;
                  case 'PAID':
                    return <Tag icon={<CheckCircle2 size={12} />} color="success">Pagada</Tag>;
                  case 'PENDING':
                    return (
                      <Tag
                        icon={<XCircle size={12} />}
                        color="error"
                        className="cursor-pointer"
                        onClick={() => {
                          setDebtModal({
                            open: true,
                            userId: group.coordinator?.id,
                            type: 'USER_COORDINATOR',
                            extraInfo: group.id,
                          });
                        }}
                      >
                        Pendiente ${comm.amount?.toLocaleString()} - Ver detalle
                      </Tag>
                    );
                  default:
                    return <Tag color="default">-</Tag>;
                }
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="Rendición Empresa">
              {(() => {
                const comm = group.companyCommissionPaid;
                if (!comm || typeof comm !== 'object') {
                  if (comm === null) return <Tag color="default">Sin pasajeros</Tag>;
                  if (comm === true) return <Tag icon={<CheckCircle2 size={12} />} color="success">Pagada</Tag>;
                  return (
                    <Tag
                      icon={<XCircle size={12} />}
                      color="error"
                      className="cursor-pointer"
                      onClick={() => {
                        setDebtModal({
                          open: true,
                          userId: group.company?.id,
                          type: 'COMPANY',
                          extraInfo: group.id,
                        });
                      }}
                    >
                      Pendiente - Ver detalle
                    </Tag>
                  );
                }
                switch (comm.status) {
                  case 'NO_PASSENGERS':
                    return <Tag icon={<AlertCircle size={12} />} color="default">Sin pasajeros</Tag>;
                  case 'NO_COMMISSION':
                    return <Tag icon={<Ban size={12} />} color="default">Comisión 0%</Tag>;
                  case 'PAID':
                    return <Tag icon={<CheckCircle2 size={12} />} color="success">Pagada</Tag>;
                  case 'PENDING':
                    return (
                      <Tag
                        icon={<XCircle size={12} />}
                        color="error"
                        className="cursor-pointer"
                        onClick={() => {
                          setDebtModal({
                            open: true,
                            userId: group.company?.id,
                            type: 'COMPANY',
                            extraInfo: group.id,
                          });
                        }}
                      >
                        Pendiente ${comm.amount?.toLocaleString()} - Ver detalle
                      </Tag>
                    );
                  default:
                    return <Tag color="default">-</Tag>;
                }
              })()}
            </Descriptions.Item>
          </Descriptions>
        )}

        <Divider orientation="left">Excursiones Asignadas</Divider>

        {assignedExcursions && assignedExcursions.length > 0 ? (
          <List
            size="small"
            dataSource={assignedExcursions}
            renderItem={(exc) => (
              <List.Item
                actions={[
                  <Button
                    key="remove"
                    type="text"
                    danger
                    size="small"
                    icon={<Trash2 size={14} />}
                    onClick={() => handleRemoveExcursion(exc.id)}
                  />,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    <Calendar size={16} className="text-blue-500 mt-1" />
                  }
                  title={exc.excursionType?.name || "Excursión"}
                  description={dayjs(exc.day).format("DD/MM/YYYY")}
                />
                <Tag color="blue">{exc.passengersCount || 0} pax</Tag>
              </List.Item>
            )}
          />
        ) : (
          <Empty
            description="No hay excursiones asignadas"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}

        <Divider orientation="left">Asignar Nueva Excursión</Divider>

        <div className="flex gap-2">
          <Select
            placeholder="Seleccionar excursión disponible"
            className="flex-1"
            value={selectedExcursion}
            onChange={setSelectedExcursion}
            loading={excursionLoading.get}
            options={availableExcursions.map((exc) => {
              const isAssigned = assignedToThisGroupIds.includes(exc.id);
              return {
                value: exc.id,
                label: `${exc.excursionType?.name || "Excursión"} - ${dayjs(exc.day).format("DD/MM/YYYY")}${isAssigned ? " ✓" : ""}`,
              };
            })}
            notFoundContent={
              availableExcursions.length === 0
                ? "No hay excursiones disponibles"
                : null
            }
          />
          <Button
            type="primary"
            onClick={handleAssignExcursion}
            disabled={!selectedExcursion}
            loading={groupLoading.post}
          >
            Asignar
          </Button>
        </div>
      </Spin>
    </Modal>

    {/* Modal de deuda */}
    <DebtModal
      open={debtModal.open}
      onClose={() => setDebtModal({ open: false, userId: null, type: null, extraInfo: null, transferId: null })}
      userId={debtModal.userId}
      type={debtModal.type}
      extraInfo={debtModal.extraInfo}
      transferId={debtModal.transferId}
      onSuccess={() => {
        setDebtModal({ open: false, userId: null, type: null, extraInfo: null, transferId: null });
        onUpdate?.();
      }}
    />
    </>
  );
}

export default GroupDetailModal;
